import { useFormik } from 'formik';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import MDButton from 'components/MDButton';
import api from 'core/interceptors/api';
import { useState } from 'react';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

// ✅ Updated Yup Validation Schema
const validationServiceTypeSchema = yup.object().shape({
  service_type: yup
    .string()
    .required('Service Type is required')
    .min(3, 'Service Type must be at least 3 characters.')
    .max(50, 'Service Type must not exceed 50 characters.')
    .matches(
      /^[A-Za-z ]+$/,
      'Service Type must only contain letters and spaces (no numbers or special characters).'
    ),
});

export default function ServiceTypeAddModel({ open, handleClose }) {
  const email = localStorage.getItem('email');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState('');

  const formik = useFormik({
    initialValues: {
      service_type: '',
      email: email,
    },
    validationSchema: validationServiceTypeSchema,
    onSubmit: async (values, { resetForm }) => {
      if (imageError) return;

      try {
        const formData = new FormData();
        formData.append('service_type', values.service_type);
        formData.append('email', email);
        if (selectedImage) {
          formData.append('service_type_image', selectedImage);
        }

        const response = await api.post(
          '/service_management/service_types/create_with_image',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );

        if (response.data.status === 1) {
          Swal.fire({
            icon: 'success',
            title: 'Service Type Added Successfully!',
            timer: 3000,
            showConfirmButton: false,
          });
        } else if (response.data.status === 2) {
          Swal.fire({
            icon: 'error',
            title: 'Service Type Already Exists!',
            timer: 3000,
            showConfirmButton: false,
          });
        }

        handleClose();
        resetForm();
        setSelectedImage(null);
        setImageError('');
      } catch (error) {
        console.error('Error adding service type:', error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to add service type',
          text: error?.response?.data?.error || 'Something went wrong',
        });
        handleClose();
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Image must be less than or equal to 2MB.');
        setSelectedImage(null);
      } else {
        setImageError('');
        setSelectedImage(file);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Service Type</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Service Type"
                name="service_type"
                fullWidth
                margin="normal"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.service_type}
                error={
                  formik.touched.service_type &&
                  Boolean(formik.errors.service_type)
                }
                helperText={
                  formik.touched.service_type &&
                  formik.errors.service_type
                }
              />
            </Grid>

            <Grid item xs={12}>
              {selectedImage && (
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxHeight: '180px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    border: '1px solid #ccc',
                  }}
                />
              )}

              <Button
                variant="contained"
                component="label"
                fullWidth
                color="info"
              >
                {selectedImage
                  ? selectedImage.name
                  : 'Upload Image (Optional, ≤ 2MB)'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>

              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              formik.resetForm();
              setSelectedImage(null);
              setImageError('');
              handleClose();
            }}
            color="primary"
          >
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={Boolean(imageError)}
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
